import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  guardianCapabilityNames,
  type ChildRequestReviewView,
  type ChildRequestView,
  type GuardianCapabilityName,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import { allCapabilities, childRequestApprovalBody, type ChildRequestMatch } from './childRequests';
import { guardianCapabilityKeys, guardianRelationshipKeys } from './guardianLinks';

export interface ChildRequestsPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

/**
 * The review queue for guardian verifiers: each child a guardian asked for, the athletes that may
 * already be that child, and the capabilities the approved link will carry.
 */
export function ChildRequestsPanel({
  locale,
  organizationId,
  csrfToken,
}: ChildRequestsPanelProps): React.JSX.Element {
  const base = `/api/v1/organizations/${organizationId}/child-requests`;
  const [requests, setRequests] = useState<ChildRequestReviewView[] | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      setRequests(await apiRequest<ChildRequestReviewView[]>(`${base}?status=pending`));
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(
    request: ChildRequestReviewView,
    path: 'approve' | 'reject',
    body: unknown,
  ): Promise<void> {
    setErrorKey(null);
    setSaved(false);
    try {
      await apiRequest<ChildRequestView>(`${base}/${request.id}/${path}`, {
        method: 'POST',
        csrfToken,
        ifMatchVersion: request.version,
        body,
      });
      setSaved(true);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
    // Reload either way: after a conflict the queue must show the current state.
    await load();
  }

  return (
    <Box component="section" aria-labelledby="child-requests-review-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="child-requests-review-title" gutterBottom>
        {translate(locale, 'childRequests.review.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'childRequests.review.intro')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved && errorKey === null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'childRequests.review.saved')}
        </Alert>
      )}
      {requests === null && errorKey === null && (
        <Typography>{translate(locale, 'childRequests.loading')}</Typography>
      )}
      {requests?.length === 0 && (
        <Typography>{translate(locale, 'childRequests.review.empty')}</Typography>
      )}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {requests?.map((request) => (
          <ChildRequestReview
            key={request.id}
            locale={locale}
            request={request}
            onApprove={(match, capabilities) =>
              decide(
                request,
                'approve',
                childRequestApprovalBody(match, capabilities, request.candidates.length > 0),
              )
            }
            onReject={(reason) => decide(request, 'reject', { reason })}
          />
        ))}
      </Stack>
    </Box>
  );
}

interface ChildRequestReviewProps {
  locale: SupportedLocale;
  request: ChildRequestReviewView;
  onApprove: (
    match: ChildRequestMatch,
    capabilities: Record<GuardianCapabilityName, boolean>,
  ) => Promise<void>;
  onReject: (reason: string) => Promise<void>;
}

const newAthleteChoice = 'new';

function ChildRequestReview({
  locale,
  request,
  onApprove,
  onReject,
}: ChildRequestReviewProps): React.JSX.Element {
  // An exact name match is the likely child, so it is preselected; anything else starts unchosen.
  const [choice, setChoice] = useState(
    request.candidates.find((candidate) => candidate.exactMatch)?.athleteId ??
      (request.candidates.length === 0 ? newAthleteChoice : ''),
  );
  const [capabilities, setCapabilities] = useState(allCapabilities);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const childName = athleteDisplayName(request, locale);
  const guardianName = athleteDisplayName(
    { givenName: request.guardianGivenName, familyName: request.guardianFamilyName },
    locale,
  );
  const match: ChildRequestMatch | null =
    choice === ''
      ? null
      : choice === newAthleteChoice
        ? { kind: 'new' }
        : { kind: 'existing', athleteId: choice };

  return (
    <Box
      component="li"
      aria-label={`${guardianName} – ${childName}`}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, padding: 2 }}
    >
      <Typography component="h3" variant="subtitle1">
        {guardianName} → {childName}
      </Typography>
      <Typography color="text.secondary">
        {translate(locale, 'athletes.dateOfBirth')}:{' '}
        {formatCalendarDate(request.dateOfBirth, locale)} ·{' '}
        {translate(locale, 'guardianLinks.relationship')}:{' '}
        {translate(locale, guardianRelationshipKeys[request.relationship])}
      </Typography>
      {request.guardianEmail !== null && (
        <Typography color="text.secondary">
          {translate(locale, 'athletes.email')}: {request.guardianEmail}
        </Typography>
      )}
      {request.note !== null && <Typography sx={{ mt: 1 }}>{request.note}</Typography>}
      <Box component="fieldset" sx={{ border: 0, padding: 0, margin: 0, mt: 2 }}>
        <FormLabel component="legend">{translate(locale, 'childRequests.review.match')}</FormLabel>
        <RadioGroup
          value={choice}
          onChange={(event) => {
            setChoice(event.target.value);
          }}
        >
          {request.candidates.map((candidate) => (
            <FormControlLabel
              key={candidate.athleteId}
              value={candidate.athleteId}
              control={<Radio />}
              label={`${athleteDisplayName(candidate, locale)} (${formatCalendarDate(candidate.dateOfBirth, locale)})${
                candidate.exactMatch ? ` – ${translate(locale, 'childRequests.review.exact')}` : ''
              }`}
            />
          ))}
          <FormControlLabel
            value={newAthleteChoice}
            control={<Radio />}
            label={translate(locale, 'childRequests.review.newAthlete')}
          />
        </RadioGroup>
        {request.candidates.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {translate(locale, 'childRequests.review.noCandidates')}
          </Typography>
        )}
      </Box>
      <Box component="fieldset" sx={{ border: 0, padding: 0, margin: 0, mt: 2 }}>
        <FormLabel component="legend">{translate(locale, 'guardianLinks.capabilities')}</FormLabel>
        <FormGroup>
          {guardianCapabilityNames.map((capability) => (
            <FormControlLabel
              key={capability}
              label={translate(locale, guardianCapabilityKeys[capability])}
              control={
                <Checkbox
                  checked={capabilities[capability]}
                  onChange={(event) => {
                    setCapabilities({ ...capabilities, [capability]: event.target.checked });
                  }}
                />
              }
            />
          ))}
        </FormGroup>
      </Box>
      {rejecting ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
          <TextField
            size="small"
            required
            label={translate(locale, 'childRequests.reason')}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            slotProps={{ htmlInput: { maxLength: 500 } }}
          />
          <Button
            size="small"
            color="error"
            variant="contained"
            disabled={reason.trim() === ''}
            onClick={() => {
              void onReject(reason.trim());
            }}
          >
            {translate(locale, 'childRequests.review.reject.confirm')}
          </Button>
          <Button
            size="small"
            onClick={() => {
              setRejecting(false);
            }}
          >
            {translate(locale, 'guardianLinks.edit.cancel')}
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            size="small"
            variant="contained"
            disabled={match === null}
            onClick={() => {
              if (match !== null) void onApprove(match, capabilities);
            }}
          >
            {translate(locale, 'childRequests.review.approve')}
          </Button>
          <Button
            size="small"
            color="warning"
            variant="outlined"
            onClick={() => {
              setRejecting(true);
            }}
          >
            {translate(locale, 'childRequests.review.reject')}
          </Button>
        </Stack>
      )}
    </Box>
  );
}
